import { ReactNode, useMemo } from 'react';
import { ThemeProvider } from 'styled-components';

import { Box } from 'components/Box';
import { PrintHidden } from 'components/PrintHidden/PrintHidden';
import { AcceptCookiesContainer } from 'containers/AcceptCookiesContainer';
import { ClientOnly } from 'containers/ClientOnly';
import FlashMessageContainer from 'containers/FlashMessageContainer';
import { ModalRoot } from 'containers/Modal/ModalRoot';
import { NoAmp } from 'containers/NoAmp';
import { colors, themeContextValue } from 'theme';
import { isDefined } from 'utils/functional';
import { getItem } from 'utils/localStorage';

import { BaseLayoutContainer } from './BaseLayout.styled';
import { ButtonBar, ButtonBarContainer } from './ButtonBar';
import { FixedSpacerContainer } from './FixedSpacerContainer';
import { PageContent } from './PageContent';
import { Root } from './Root';

const testMode = getItem('testMode');

type CSSPosition = 'static' | 'sticky';

type Props = {
  children: ReactNode;
  header?: ReactNode;
  buttonBar?: ReactNode;
  centered?: boolean;
  stickyHeaderHeight?: (string | number)[];
};

export function BaseLayout({
  children,
  header,
  buttonBar,
  centered,
  stickyHeaderHeight,
}: Props) {
  const flashMessageContainerPosition: CSSPosition | CSSPosition[] =
    useMemo(() => {
      if (testMode || !isDefined(stickyHeaderHeight)) return 'static';

      if (typeof stickyHeaderHeight === 'number') return 'sticky';

      return stickyHeaderHeight.map((height) =>
        isDefined(height) ? 'sticky' : 'static',
      );
    }, [stickyHeaderHeight]);

  return (
    <ThemeProvider theme={themeContextValue}>
      <Root
        style={{ background: centered ? colors.backgroundGrey : undefined }}
        id="layout-root"
      >
        <BaseLayoutContainer>
          {header}

          <ClientOnly>
            <Box
              data-qa-id="some-element"
              position={flashMessageContainerPosition}
              top={stickyHeaderHeight}
              zIndex={3}
            >
              <FlashMessageContainer />
            </Box>
          </ClientOnly>

          <PageContent $centered={centered}>{children}</PageContent>
        </BaseLayoutContainer>

        {buttonBar && (
          <NoAmp>
            <PrintHidden>
              <ButtonBarContainer>
                <ButtonBar>{buttonBar}</ButtonBar>
              </ButtonBarContainer>
            </PrintHidden>
          </NoAmp>
        )}

        <FixedSpacerContainer>
          <ClientOnly>
            <AcceptCookiesContainer />
          </ClientOnly>
        </FixedSpacerContainer>

        <ModalRoot />
      </Root>
    </ThemeProvider>
  );
}
